// snapshot.ts — the /api/snapshot contract, shared by the ingest worker (which
// builds it) and the Pages Functions (which serve it) and the frontend (which
// boots from it). Everything the prototype used to hardcode lives here, plus
// per-field provenance and an `asOf`. Pure: no I/O.

import type { Drivers, Source, World } from "./model/entities.ts";
import { baseDrivers, baseWorld } from "./model/seed.ts";
import { SCEN, type Scenario, type ScenarioId } from "./model/scenarios.ts";
import { coldStartBaseline } from "./econ/baseline.ts";
import type { EconBaseline } from "./econ/types.ts";

export interface FieldProvenance {
  source: Source;
  /** ISO-8601 of when this value was last known good. */
  asOf?: string;
  /** For EDGAR: which XBRL tag actually resolved (CLAUDE.md §11.4). */
  tagUsed?: string;
}

export interface SnapshotProvenance {
  yields: { y10: FieldProvenance; y20: FieldProvenance; y30: FieldProvenance };
  spreadMultiple: FieldProvenance;
  gasShare: FieldProvenance;
  powerPrice: FieldProvenance;
  /** Keyed by entity id (hyperscaler) or name (supplier). */
  entities: Record<string, { revenue?: FieldProvenance; capex?: FieldProvenance }>;
}

export interface SnapshotPayload {
  /** Overall newest `asOf` across all live fields. Shown in the header. */
  asOf: string;
  /** When this snapshot document was assembled. */
  generatedAt: string;
  /** The entity literals + coefficients the frontend solves against. */
  world: World;
  /** Default slider positions: base case, overridden by live values. */
  drivers: Drivers;
  provenance: SnapshotProvenance;
  scenarios: Record<ScenarioId, Scenario>;
  /**
   * Labor & growth baseline, shared by the /scenarios module. Each field carries
   * its own provenance (plan §5). Optional so the capex-only contract is
   * unchanged for anything that predates the econ module.
   */
  econ?: EconBaseline;
  /** Non-sensitive run metadata for the /data page. No secrets, no stack traces. */
  meta?: { errors?: Array<{ series: string; error: string }> };
  /** True if ANY field is STALE — frontend shows a banner (INV-5). */
  stale: boolean;
}

/** Every field ESTIMATE/GUIDANCE by default (cold start, task.md 1.4). */
function seedProvenance(world: World): SnapshotProvenance {
  const g = (source: Source): FieldProvenance => ({ source });
  const entities: SnapshotProvenance["entities"] = {};
  for (const h of world.HYP) entities[h.id] = { revenue: g("GUIDANCE"), capex: g("GUIDANCE") };
  for (const s of world.SUP) entities[s.n] = { revenue: g("ESTIMATE") };
  return {
    yields: { y10: g("ESTIMATE"), y20: g("ESTIMATE"), y30: g("ESTIMATE") },
    spreadMultiple: g("ESTIMATE"),
    gasShare: g("ESTIMATE"),
    powerPrice: g("ESTIMATE"),
    entities,
  };
}

/**
 * Cold-start snapshot: seed world, base drivers, everything ESTIMATE/GUIDANCE.
 * The app is fully functional with zero live data — this is the fallback the
 * API serves before any ingest has run, and the base the ingest upgrades.
 */
export function coldStartSnapshot(generatedAt: string): SnapshotPayload {
  const world = baseWorld();
  return {
    asOf: generatedAt,
    generatedAt,
    world,
    drivers: baseDrivers(),
    provenance: seedProvenance(world),
    scenarios: SCEN,
    econ: coldStartBaseline(),
    meta: { errors: [] },
    stale: false,
  };
}

/** True if any provenance field is STALE (drives the frontend banner). */
export function hasStale(p: SnapshotProvenance): boolean {
  const fields = [p.yields.y10, p.yields.y20, p.yields.y30, p.spreadMultiple, p.gasShare, p.powerPrice];
  for (const f of fields) if (f.source === "STALE") return true;
  for (const e of Object.values(p.entities)) {
    if (e.revenue?.source === "STALE" || e.capex?.source === "STALE") return true;
  }
  return false;
}

/** True if any econ baseline field is STALE (also drives the frontend banner). */
export function hasStaleEcon(econ: EconBaseline | undefined): boolean {
  if (!econ) return false;
  for (const field of Object.values(econ)) if (field.flag === "STALE") return true;
  return false;
}
